// src/pages/user/UserPayments.tsx
// Route: /user/payments

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useGetPaymentsQuery, useRequestRefundMutation } from "../../app/api/parentApi";
import { useInitializePaymentMutation } from "../../app/api/paymentApi";
import type {
  PaymentSummary,
  PaymentEnrolmentContext,
  GetPaymentsParams,
  PayStatus,
} from "../../app/api/parentApi";

import { ArrowLeft, ArrowRight, CalendarDays, Check, CreditCard, FileText, Hourglass, Mail, MessageCircle, RotateCw, TriangleAlert, Users, X } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── CONSTANTS ──────────────────────────────────────────────────── */

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; border: string; dot: string }
> = {
  paid: {
    label: "Paid",
    bg: "#edfaf4",
    text: "#2a7a56",
    border: "#b6e8d0",
    dot: "#6cc2a0",
  },
  pending: {
    label: "Pending",
    bg: "#fff8ee",
    text: "#7a4a10",
    border: "#f5d9a0",
    dot: "#f0a050",
  },
  failed: {
    label: "Failed",
    bg: "#fff3f3",
    text: "#9b2a2a",
    border: "#f5c0c0",
    dot: "#e57373",
  },
  refunded: {
    label: "Refunded",
    bg: "#f1f3f8",
    text: "#4a44b0",
    border: "#cdd4e2",
    dot: "#1b2d5e",
  },
  overdue: {
    label: "Overdue",
    bg: "#fff3f3",
    text: "#9b2a2a",
    border: "#f5c0c0",
    dot: "#e57373",
  },
  waived: {
    label: "Waived",
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#c9cfe0",
    dot: "#1b2d5e",
  },
};

const BILLING_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; border: string }
> = {
  monthly: {
    label: "Monthly",
    bg: "#fff8ee",
    text: "#7a4a10",
    border: "#f5d9a0",
  },
  quarterly: {
    label: "Quarterly",
    bg: "#ebeff7",
    text: "#4a44b0",
    border: "#cdd4e2",
  },
  annually: {
    label: "Annual",
    bg: "#edfaf4",
    text: "#2a7a56",
    border: "#b6e8d0",
  },
};

/* ─── HELPERS ────────────────────────────────────────────────────── */

function formatNaira(n: number) {
  return `₦${Number(n).toLocaleString("en-NG")}`;
}
function childInitials(c: PaymentSummary["child"]) {
  if (!c) return "?";
  return `${c.firstName.charAt(0)}${c.lastName.charAt(0)}`.toUpperCase();
}
function childName(c: PaymentSummary["child"]) {
  if (!c) return "Unknown";
  return `${c.firstName} ${c.lastName}`;
}
function asEnrolment(
  e: PaymentSummary["enrolment"],
): PaymentEnrolmentContext | null {
  return e && typeof e === "object" ? (e as PaymentEnrolmentContext) : null;
}

/* ─── ATOMS ──────────────────────────────────────────────────────── */

function StatusChip({ status }: { status: string }) {
  const c = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: c.dot }}
      />
      {c.label}
    </span>
  );
}

function BillingChip({ period }: { period: string | null }) {
  if (!period) return null;
  const c = BILLING_CONFIG[period] ?? BILLING_CONFIG.monthly;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold"
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
      }}
    >
      {c.label}
    </span>
  );
}

function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />
  );
}
function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
      />
    </svg>
  );
}

/* ─── STATS BAR ──────────────────────────────────────────────────── */

interface Stats {
  totalPaid: number;
  totalPending: number;
  countPaid: number;
  countPending: number;
  countFailed: number;
  countAll: number;
}

function computeStats(payments: PaymentSummary[]): Stats {
  return payments.reduce<Stats>(
    (acc, p) => {
      acc.countAll++;
      if (p.status === "paid") {
        acc.totalPaid += p.amountRaw;
        acc.countPaid++;
      }
      if (p.status === "pending") {
        acc.totalPending += p.amountRaw;
        acc.countPending++;
      }
      if (p.status === "failed" || p.status === "overdue") {
        acc.countFailed++;
      }
      return acc;
    },
    {
      totalPaid: 0,
      totalPending: 0,
      countPaid: 0,
      countPending: 0,
      countFailed: 0,
      countAll: 0,
    },
  );
}

function StatsBar({ stats, isLoading }: { stats: Stats; isLoading: boolean }) {
  if (isLoading)
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[80px] rounded-[20px]" />
        ))}
      </div>
    );
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {[
        {
          label: "Total paid",
          value: formatNaira(stats.totalPaid),
          sub: `${stats.countPaid} payment${stats.countPaid !== 1 ? "s" : ""}`,
          icon: Check,
          iconBg: "#edfaf4",
          iconClr: "#6cc2a0",
        },
        {
          label: "Pending",
          value: formatNaira(stats.totalPending),
          sub: `${stats.countPending} awaiting`,
          icon: Hourglass,
          iconBg: "#fff8ee",
          iconClr: "#f0a050",
        },
        {
          label: "Issues",
          value: String(stats.countFailed),
          sub: "failed / overdue",
          icon: X,
          iconBg: "#fff3f3",
          iconClr: "#e57373",
        },
        {
          label: "All records",
          value: String(stats.countAll),
          sub: "total transactions",
          icon: Users,
          iconBg: "#ebeff7",
          iconClr: "#1b2d5e",
        },
      ].map((item) => (
        <div
          key={item.label}
          className="flex items-center gap-3.5 rounded-[20px] border border-[#e5e8ef] bg-white px-4 py-3.5 shadow-[0_2px_8px_rgba(27,45,94,0.04)]"
        >
          <span
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[11px] text-sm font-bold"
            style={{ background: item.iconBg, color: item.iconClr }}
          >
            <Ico icon={item.icon} />
          </span>
          <div className="min-w-0">
            <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0] truncate">
              {item.label}
            </p>
            <p className="mt-0.5 text-base font-bold tracking-[-0.02em] text-[#16213e] truncate">
              {item.value}
            </p>
            <p className="text-[11.5px] text-[#a6adbf]">{item.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── 14-DAY MONEY-BACK GUARANTEE ──────────────────────────────── */

function MoneyBackPanel({ payment }: { payment: PaymentSummary }) {
  const mb = payment.moneyBack;
  const [ask, setAsk] = useState(false);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [requestRefund, { isLoading }] = useRequestRefundMutation();
  if (!mb || (!mb.eligible && !mb.request)) return null;
  const day = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" }) : "";

  if (mb.request) {
    const r = mb.request;
    const text =
      r.status === "requested"
        ? `You asked for a refund on ${day(r.requestedAt)}. We'll email you, usually within two working days.`
        : r.status === "approved"
          ? `Refund approved on ${day(r.decidedAt)}. Card refunds usually arrive within 5 to 10 working days.`
          : `Your refund request was declined on ${day(r.decidedAt)}.`;
    return (
      <div className={`rounded-[18px] border px-4 py-3 text-sm ${r.status === "declined" ? "border-[#f5c5b0] bg-[#fff4f1] text-[#7a3020]" : "border-[#b6e8d0] bg-[#edfaf4] text-[#1f5c3f]"}`}>
        <p className="font-bold">14-day money-back guarantee</p>
        <p className="mt-1 leading-6">{text}</p>
        {r.note && <p className="mt-1 leading-6"><b>Note:</b> {r.note}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-[18px] border border-[#e5e8ef] bg-[#f9fafc] px-4 py-3 text-sm text-[#3b4560]">
      <p className="font-bold text-[#16213e]">14-day money-back guarantee</p>
      <p className="mt-1 leading-6">
        Not the right fit? Ask for a full refund of this first payment until {day(mb.until)}.
      </p>
      {msg?.ok ? (
        <p className="mt-2 font-semibold text-[#2a7a56]">{msg.text}</p>
      ) : ask ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What didn't work for you? (optional, but it helps us improve)"
            className="min-h-[70px] w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]"
          />
          {msg && <p className="font-semibold text-[#b94a3a]">{msg.text}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={isLoading}
              onClick={async () => {
                setMsg(null);
                try {
                  const res = await requestRefund({ id: payment.id, reason: reason.trim() || undefined }).unwrap();
                  setMsg({ ok: true, text: res.message ?? "We have your request." });
                } catch (err) {
                  const e = err as { data?: { error?: string } };
                  setMsg({ ok: false, text: e?.data?.error ?? "Could not send the request" });
                }
              }}
              className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {isLoading ? "Sending…" : "Ask for a refund"}
            </button>
            <button type="button" onClick={() => setAsk(false)} className="rounded-xl px-3 py-2 text-sm font-semibold text-[#5f6780] hover:bg-[#ebeff7]">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAsk(true)} className="mt-2 text-sm font-bold text-[#1b2d5e] underline-offset-2 hover:underline">
          Request a refund
        </button>
      )}
    </div>
  );
}

/* ─── PAYMENT DETAIL DRAWER ──────────────────────────────────────── */

function PaymentDetailDrawer({
  payment,
  onClose,
}: {
  payment: PaymentSummary;
  onClose: () => void;
}) {
  const [initPay, { isLoading: retrying }] = useInitializePaymentMutation();
  const [retryError, setRetryError] = useState("");
  const enr = asEnrolment(payment.enrolment);
  const enrolmentId =
    enr?.id ??
    (typeof payment.enrolment === "string" ? payment.enrolment : null);
  const canRetry =
    (payment.status === "pending" || payment.status === "failed") &&
    !!enrolmentId;
  const cfg = STATUS_CONFIG[payment.status] ?? STATUS_CONFIG.pending;

  const handleRetry = async () => {
    if (!enrolmentId) return;
    setRetryError("");
    try {
      const result = await initPay({ enrolmentId }).unwrap();
      window.location.href = result.data.authorizationUrl;
    } catch (err: any) {
      setRetryError(err?.data?.error ?? "Payment initiation failed.");
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[420px]"
        role="dialog"
        aria-modal
        aria-label="Payment detail"
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold tracking-[-0.03em] text-[#16213e]">
              Payment detail
            </h2>
            <p className="mt-0.5 font-mono text-[12.5px] text-[#8189a0]">
              {payment.reference ?? "No reference"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8] hover:text-[#16213e]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Amount hero */}
          <div
            className="rounded-[20px] p-5 text-center"
            style={{
              background: `linear-gradient(135deg, ${cfg.bg} 0%, white 100%)`,
              border: `1px solid ${cfg.border}`,
            }}
          >
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <StatusChip status={payment.status} />
              {enr?.billingPeriod && <BillingChip period={enr.billingPeriod} />}
            </div>
            <p className="mt-3 text-4xl font-bold tracking-[-0.04em] text-[#16213e]">
              {payment.amount}
            </p>
            <p className="mt-1.5 text-xs text-[#8189a0]">{payment.desc}</p>
          </div>

          {/* Enrolment context card */}
          {enr && (
            <div className="rounded-[18px] border border-[#dde2ec] bg-[#f9fafc] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#ebeff7] flex items-center justify-between">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  Enrolment
                </p>
                <BillingChip period={enr.billingPeriod} />
              </div>
              <div className="divide-y divide-[#f7f8fb]">
                {[
                  { label: "Plan", value: enr.billingLabel ?? "—" },
                  {
                    label: "Quarter",
                    value: `${enr.quarter ?? "—"} · ${enr.academicYear ?? ""}`,
                  },
                  {
                    label: "Track",
                    value: (enr.track ?? "").replace(/_/g, " "),
                  },
                  {
                    label: "Months",
                    value: enr.monthsCovered
                      ? `${enr.monthsCovered} month${enr.monthsCovered > 1 ? "s" : ""}`
                      : "—",
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between px-4 py-2.5"
                  >
                    <p className="text-xs text-[#8189a0]">{row.label}</p>
                    <p className="text-xs font-semibold text-[#16213e] capitalize">
                      {row.value}
                    </p>
                  </div>
                ))}
              </div>
              {/* Pricing breakdown */}
              {enr.grossAmount !== null && (enr.discountPercent > 0 || (enr.familyDiscountPercent ?? 0) > 0) && (
                <div className="border-t border-[#ebeff7] px-4 py-3 space-y-1.5">
                  <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0] mb-2">
                    Pricing
                  </p>
                  <div className="flex justify-between text-xs text-[#5a6278]">
                    <span>Subtotal ({enr.monthsCovered} × base)</span>
                    <span>{formatNaira(enr.grossAmount)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-[#6cc2a0] font-semibold">
                    <span>
                      {enr.billingLabel} discount ({enr.discountPercent}%)
                    </span>
                    <span>−{formatNaira(enr.discountAmount)}</span>
                  </div>
                  {(enr.familyDiscountPercent ?? 0) > 0 && (
                    <div className="flex justify-between text-xs text-[#6cc2a0] font-semibold">
                      <span>Family discount ({enr.familyDiscountPercent}%)</span>
                      <span>−{formatNaira(enr.familyDiscountAmount ?? 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold text-[#16213e] border-t border-[#ebeff7] pt-1.5">
                    <span>Total paid</span>
                    <span>
                      {enr.feeAmount !== null
                        ? formatNaira(enr.feeAmount)
                        : payment.amount}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <MoneyBackPanel payment={payment} />

          {/* Payment details */}
          <div className="rounded-[18px] border border-[#e5e8ef] divide-y divide-[#f7f8fb] overflow-hidden">
            {[
              { label: "Child", value: childName(payment.child) },
              { label: "Date", value: payment.date },
              {
                label: "Paid on",
                value: payment.paidAt
                  ? new Date(payment.paidAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "—",
              },
              { label: "Currency", value: payment.currency },
            ].map((row) => (
              <div
                key={row.label}
                className="flex justify-between bg-white px-4 py-3"
              >
                <p className="text-xs font-semibold text-[#8189a0]">
                  {row.label}
                </p>
                <p className="text-sm font-semibold text-[#16213e]">
                  {row.value}
                </p>
              </div>
            ))}
          </div>

          {/* Next payment date */}
          {payment.nextPaymentLabel && payment.status === "paid" && (
            <div className="rounded-[16px] border border-[#cdd4e2] bg-[#ebeff7] px-4 py-3.5 flex items-center gap-3">
              <CalendarDays className="h-5 w-5 flex-shrink-0" aria-hidden />
              <div>
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#5a6278]">
                  Next payment due
                </p>
                <p className="mt-0.5 text-sm font-bold text-[#16213e]">
                  {payment.nextPaymentLabel}
                </p>
              </div>
            </div>
          )}

          {/* Reference */}
          {payment.reference && (
            <div className="rounded-[16px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3.5">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Reference
              </p>
              <p className="mt-1 break-all font-mono text-sm text-[#3b4560]">
                {payment.reference}
              </p>
            </div>
          )}

          {retryError && (
            <div className="rounded-[16px] border border-[#fde0d9] bg-[#fff5f3] px-4 py-3">
              <p className="text-xs text-[#b94a3a]">{retryError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 flex-col gap-2.5 border-t border-[#ebeff7] px-6 py-5">
          {payment.receiptUrl && (
            <a
              href={payment.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-2xl border border-[#dde2ec] py-3 text-sm font-semibold text-[#3b4560] transition hover:bg-[#f1f3f8]"
            >
              <FileText className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Download receipt
            </a>
          )}
          {canRetry && (
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="flex items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.28)] transition hover:bg-[#24397a] disabled:opacity-60"
            >
              {retrying ? (
                <>
                  <Spinner /> Redirecting…
                </>
              ) : (
                <><CreditCard className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Retry payment</>
              )}
            </button>
          )}
          {enrolmentId && (
            <Link
              to="/user/enrolments"
              className="flex items-center justify-center rounded-2xl border border-[#dde2ec] py-2.5 text-xs font-semibold text-[#5a6278] transition hover:bg-[#f1f3f8]"
            >
              View enrolment{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </Link>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── PAYMENT ROW (desktop table) ────────────────────────────────── */

function PaymentRow({
  payment,
  onClick,
}: {
  payment: PaymentSummary;
  onClick: () => void;
}) {
  const enr = asEnrolment(payment.enrolment);
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-[#f7f8fb] transition-colors hover:bg-[#f9fafc]"
    >
      {/* Child + description */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#ebeff7] text-[12.5px] font-bold text-[#1b2d5e]">
            {childInitials(payment.child)}
          </span>
          <div>
            <p className="text-sm font-semibold text-[#16213e]">
              {payment.desc}
            </p>
            <p className="text-[12.5px] text-[#8189a0]">
              {childName(payment.child)}
            </p>
          </div>
        </div>
      </td>
      {/* Amount */}
      <td className="px-5 py-4">
        <p className="text-sm font-bold text-[#16213e]">{payment.amount}</p>
        {enr?.discountPercent ? (
          <p className="text-[11.5px] text-[#6cc2a0] font-semibold">
            {enr.discountPercent}% off
          </p>
        ) : null}
      </td>
      {/* Billing plan */}
      <td className="px-5 py-4">
        <div className="flex flex-col gap-1">
          <BillingChip period={enr?.billingPeriod ?? null} />
          {enr && (
            <p className="text-[11.5px] text-[#8189a0]">
              {enr.quarter} · {enr.academicYear}
            </p>
          )}
        </div>
      </td>
      {/* Date */}
      <td className="px-5 py-4">
        <p className="text-xs text-[#5a6278]">{payment.date}</p>
        {payment.nextPaymentLabel && payment.status === "paid" && (
          <p className="text-[11.5px] text-[#1b2d5e] font-medium">
            <RotateCw className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{payment.nextPaymentLabel}
          </p>
        )}
      </td>
      {/* Status */}
      <td className="px-5 py-4">
        <StatusChip status={payment.status} />
      </td>
      {/* Chevron */}
      <td className="px-4 py-4">
        <svg className="h-4 w-4 text-[#c9cfe0]" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </td>
    </tr>
  );
}

/* ─── PAYMENT CARD (mobile) ──────────────────────────────────────── */

function PaymentCard({
  payment,
  onClick,
}: {
  payment: PaymentSummary;
  onClick: () => void;
}) {
  const enr = asEnrolment(payment.enrolment);
  return (
    <button
      onClick={onClick}
      className="w-full rounded-[20px] border border-[#e5e8ef] bg-white p-4 text-left shadow-[0_2px_8px_rgba(27,45,94,0.04)] transition hover:shadow-[0_4px_16px_rgba(27,45,94,0.08)] hover:border-[#c9cfe0]"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[12px] bg-[#ebeff7] text-xs font-bold text-[#1b2d5e]">
          {childInitials(payment.child)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-bold text-[#16213e] truncate">
              {payment.desc}
            </p>
            <p className="flex-shrink-0 text-sm font-bold text-[#16213e]">
              {payment.amount}
            </p>
          </div>
          <p className="mt-0.5 text-xs text-[#8189a0]">
            {childName(payment.child)}
          </p>
          {/* Billing plan + enrolment context */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {enr?.billingPeriod && <BillingChip period={enr.billingPeriod} />}
            {enr && (
              <span className="text-[11.5px] text-[#8189a0]">
                {enr.quarter} · {enr.academicYear}
              </span>
            )}
            {enr?.discountPercent ? (
              <span className="text-[11.5px] font-semibold text-[#6cc2a0]">
                {enr.discountPercent}% off
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div>
              <p className="text-[12.5px] text-[#8189a0]">{payment.date}</p>
              {payment.nextPaymentLabel && payment.status === "paid" && (
                <p className="text-[11.5px] font-medium text-[#1b2d5e]">
                  <RotateCw className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Next: {payment.nextPaymentLabel}
                </p>
              )}
            </div>
            <StatusChip status={payment.status} />
          </div>
        </div>
      </div>
    </button>
  );
}

/* ─── FILTER BAR ─────────────────────────────────────────────────── */

interface Filters {
  status: string;
  childId: string;
  search: string;
}

function FilterBar({
  filters,
  onChange,
  children,
  count,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  children: Array<{ id: string; firstName: string; lastName: string }>;
  count: number;
}) {
  const sel =
    "rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs font-semibold text-[#3b4560] outline-none transition focus:border-[#1b2d5e] cursor-pointer";
  const hasFilters = filters.status || filters.childId || filters.search;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px]">
        <svg
          className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#a6adbf]"
          viewBox="0 0 16 16"
          fill="none"
        >
          <circle
            cx="6.5"
            cy="6.5"
            r="4.5"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M10 10l3 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <input
          type="text"
          placeholder="Search by name or reference…"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          className="w-full rounded-xl border border-[#dde2ec] bg-white py-2 pl-8 pr-3 text-xs text-[#16213e] placeholder:text-[#a6adbf] outline-none transition focus:border-[#1b2d5e]"
        />
      </div>
      <select
        value={filters.status}
        onChange={(e) => onChange({ ...filters, status: e.target.value })}
        className={sel}
      >
        <option value="">All statuses</option>
        <option value="paid">Paid</option>
        <option value="pending">Pending</option>
        <option value="failed">Failed</option>
        <option value="refunded">Refunded</option>
        <option value="overdue">Overdue</option>
      </select>
      {children.length > 1 && (
        <select
          value={filters.childId}
          onChange={(e) => onChange({ ...filters, childId: e.target.value })}
          className={sel}
        >
          <option value="">All children</option>
          {children.map((c) => (
            <option key={c.id} value={c.id}>
              {c.firstName} {c.lastName}
            </option>
          ))}
        </select>
      )}
      <p className="text-xs font-semibold text-[#8189a0]">
        {count} record{count !== 1 ? "s" : ""}
      </p>
      {hasFilters && (
        <button
          onClick={() => onChange({ status: "", childId: "", search: "" })}
          className="text-[12.5px] font-semibold text-[#1b2d5e] hover:text-[#4a44b0]"
        >
          Clear <X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </button>
      )}
    </div>
  );
}

/* ─── PAGINATION ─────────────────────────────────────────────────── */

function Pagination({
  page,
  pages,
  total,
  count,
  onPage,
}: {
  page: number;
  pages: number;
  total: number;
  count: number;
  onPage: (p: number) => void;
}) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between">
      <p className="text-xs text-[#8189a0]">
        Showing {count} of {total}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8] disabled:opacity-40"
        >
         {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Prev
        </button>
        {Array.from({ length: Math.min(pages, 5) }).map((_, i) => {
          const p = i + 1;
          return (
            <button
              key={p}
              onClick={() => onPage(p)}
              className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${p === page ? "bg-[#1b2d5e] text-white" : "border border-[#dde2ec] text-[#3b4560] hover:bg-[#f1f3f8]"}`}
            >
              {p}
            </button>
          );
        })}
        <button
          onClick={() => onPage(page + 1)}
          disabled={page === pages}
          className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8] disabled:opacity-40"
        >
          Next{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function UserPayments() {
  const [selectedPayment, setSelectedPayment] = useState<PaymentSummary | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    status: "",
    childId: "",
    search: "",
  });

  const queryParams: GetPaymentsParams = {
    page,
    limit: 20,
    ...(filters.status ? { status: filters.status as PayStatus } : {}),
    ...(filters.childId ? { childId: filters.childId } : {}),
  };

  const { data, isLoading, isError, refetch } =
    useGetPaymentsQuery(queryParams);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allPayments: PaymentSummary[] = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!filters.search.trim()) return allPayments;
    const q = filters.search.trim().toLowerCase();
    return allPayments.filter(
      (p) =>
        p.reference?.toLowerCase().includes(q) ||
        p.desc.toLowerCase().includes(q) ||
        childName(p.child).toLowerCase().includes(q),
    );
  }, [allPayments, filters.search]);

  const uniqueChildren = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ id: string; firstName: string; lastName: string }> =
      [];
    for (const p of allPayments) {
      if (p.child && !seen.has(p.child.id)) {
        seen.add(p.child.id);
        result.push(p.child);
      }
    }
    return result;
  }, [allPayments]);

  const stats = useMemo(() => computeStats(allPayments), [allPayments]);
  const pending = allPayments.filter((p) => p.status === "pending").length;

  const handleFilterChange = (f: Filters) => {
    setFilters(f);
    setPage(1);
  };

  if (isError)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          Couldn't load payments
        </p>
        <button
          onClick={refetch}
          className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white"
        >
          Retry
        </button>
      </div>
    );

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">
            Payments
          </h1>
          <p className="mt-1 text-sm text-[#5f6780]">
            All payment records for your children's enrolments.
          </p>
        </div>
        <Link
          to="/user/enrolments"
          className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-2xl border border-[#dde2ec] px-4 py-2.5 text-sm font-semibold text-[#3b4560] transition hover:border-[#c9cfe0] hover:bg-[#f1f3f8]"
        >
          View enrolments{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </Link>
      </div>

      {/* Pending alert */}
      {!isLoading && pending > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-[20px] border border-[#f5d9a0] bg-[#fff8ee] px-5 py-4">
          <TriangleAlert className="h-5 w-5 flex-shrink-0" aria-hidden />
          <div className="flex-1">
            <p className="text-sm font-bold text-[#7a4a10]">
              {pending} payment{pending !== 1 ? "s" : ""} awaiting completion
            </p>
            <p className="mt-0.5 text-xs text-[#9a6a20]">
              Click a pending payment to complete checkout via Paystack.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <StatsBar stats={stats} isLoading={isLoading} />

      {/* Filters */}
      {!isLoading && allPayments.length > 0 && (
        <div className="mt-5">
          <FilterBar
            filters={filters}
            onChange={handleFilterChange}
            children={uniqueChildren}
            count={filtered.length}
          />
        </div>
      )}

      {/* List */}
      <div className="mt-6">
        {isLoading ? (
          <>
            <div className="space-y-3 md:hidden">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[108px] rounded-[20px]" />
              ))}
            </div>
            <div className="hidden md:block space-y-0 overflow-hidden rounded-[24px] border border-[#e5e8ef]">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 border-b border-[#f7f8fb] px-5 py-4 last:border-0 bg-white"
                >
                  <Skeleton className="h-8 w-8 rounded-[10px]" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-48" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              ))}
            </div>
          </>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ebeff7] text-3xl">
              <CreditCard className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </span>
            <h3 className="mt-5 text-lg font-bold text-[#16213e]">
              {filters.status || filters.childId || filters.search
                ? "No payments match this filter"
                : "No payment records yet"}
            </h3>
            <p className="mt-2 max-w-xs text-sm leading-6 text-[#5f6780]">
              {filters.status || filters.childId || filters.search
                ? "Try clearing the filters."
                : "Payment records appear once you enrol a child and complete payment."}
            </p>
            {filters.status || filters.childId || filters.search ? (
              <button
                onClick={() =>
                  setFilters({ status: "", childId: "", search: "" })
                }
                className="mt-4 text-sm font-semibold text-[#1b2d5e]"
              >
                Clear filters
              </button>
            ) : (
              <Link
                to="/user/enrolments"
                className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.28)] hover:bg-[#24397a]"
              >
                View enrolments{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {filtered.map((p) => (
                <PaymentCard
                  key={p.id}
                  payment={p}
                  onClick={() => setSelectedPayment(p)}
                />
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-[24px] border border-[#e5e8ef] bg-white shadow-[0_4px_16px_rgba(27,45,94,0.05)] md:block">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[#f7f8fb]">
                    {[
                      "Description",
                      "Amount",
                      "Billing plan",
                      "Date / Next due",
                      "Status",
                      "",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-5 py-3.5 text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <PaymentRow
                      key={p.id}
                      payment={p}
                      onClick={() => setSelectedPayment(p)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {(data?.pages ?? 1) > 1 && (
              <div className="mt-5">
                <Pagination
                  page={data?.page ?? 1}
                  pages={data?.pages ?? 1}
                  total={data?.total ?? 0}
                  count={filtered.length}
                  onPage={(p) => {
                    setPage(p);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Help */}
      {!isLoading && allPayments.length > 0 && (
        <div className="mt-8 rounded-[24px] border border-[#e5e8ef] bg-white px-6 py-5">
          <p className="text-sm font-bold text-[#16213e]">
            Need help with a payment?
          </p>
          <p className="mt-1 text-xs leading-5 text-[#5f6780]">
            Contact the academy with your reference number for any payment query
            or refund request.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href="mailto:hello@futuresmartlab.com"
              className="inline-flex items-center gap-1.5 rounded-2xl border border-[#dde2ec] px-4 py-2 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
            >
              <Mail className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Email support
            </a>
            <Link
              to="/user/messages"
              className="inline-flex items-center gap-1.5 rounded-2xl border border-[#dde2ec] px-4 py-2 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
            >
              <MessageCircle className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Send a message
            </Link>
          </div>
        </div>
      )}

      {/* Drawer */}
      {selectedPayment && (
        <PaymentDetailDrawer
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </>
  );
}
